// Présences (M11) : feuille d'un jour (RH pour tous, chef pour les agents de ses services), une ligne par
// agent et date ; import CSV tout ou rien avec rapport par numéro de ligne (RH) ; exports CSV des présences
// et des absences pour la paie externe (RH), établis par le serveur avec la même requête que la liste.
import { ETATS_PRESENCE, LIBELLES_ETAT_PRESENCE, type PresenceVue, type RapportImportPresences } from '@cc/contrats';
import { Bandeau, Bouton, Champ, EtatVide, FichierChamp, LienBouton, Panneau, Selection, Tableau, type ColonneTableau } from '@cc/ui';
import { ArrowLeft, ClipboardList, Download } from 'lucide-react';
import { useState } from 'react';
import { ErreurApi } from '../../api/erreurs';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { importerPresences, INVALIDER_PERSONNEL, saisirPresences, telechargerExport, useFeuille } from './api';
import { aujourdhuiIso, LIBELLES_ERREUR_IMPORT } from './logique';
import commun from '../../commun/commun.module.css';
import styles from '../proprete/proprete.module.css';

const OPTIONS_ETAT = ETATS_PRESENCE.map((e) => ({ valeur: e, libelle: LIBELLES_ETAT_PRESENCE[e] }));

function Feuille() {
  const [jour, setJour] = useState(aujourdhuiIso());
  const feuille = useFeuille(jour, '');
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  const enregistrer = useAction({
    executer: () => saisirPresences({ jour, lignes: Object.entries(saisie).map(([agent_id, etat]) => ({ agent_id, etat })) }),
    succes: (r) => `Présences du ${r.jour} enregistrées.`,
    invalider: INVALIDER_PERSONNEL,
    apres: () => setSaisie({}),
  });
  const colonnes: ColonneTableau<PresenceVue>[] = [
    { id: 'agent', entete: 'Agent', enTeteLigne: true, masquable: false, cellule: (p) => `${p.nom} ${p.prenoms} (${p.matricule})`, valeurTri: (p) => p.nom },
    { id: 'service', entete: 'Service', cellule: (p) => p.service_nom ?? 'sans affectation' },
    { id: 'etat', entete: 'État', cellule: (p) => (
      <Selection libelle={`État de ${p.nom} ${p.prenoms}`} options={OPTIONS_ETAT} optionVide="Non saisi" value={saisie[p.agent_id] ?? p.etat ?? ''}
        onChange={(e) => setSaisie((s) => ({ ...s, [p.agent_id]: e.target.value }))} />
    ) },
  ];
  const lignes = feuille.data?.elements ?? [];
  return (
    <Panneau titre="Feuille de présence" niveau={2}>
      <Champ libelle="Jour" type="date" value={jour} onChange={(e) => { setJour(e.target.value); setSaisie({}); }} />
      <EtatRequete chargement={feuille.isPending} erreur={feuille.error} onReessayer={() => void feuille.refetch()} lot="L5-A" fonction="La feuille de présence">
        {lignes.length === 0 ? <EtatVide titre="Aucun agent affecté ce jour" icone={ClipboardList}><p>Aucun agent de votre périmètre n&apos;a d&apos;affectation en cours à cette date.</p></EtatVide> : (
          <>
            <Tableau legende={`Présences du ${jour}`} colonnes={colonnes} lignes={lignes} cleLigne={(p) => p.agent_id} cleColonnes="personnel-feuille" />
            {enregistrer.messageErreur ? <Bandeau type="erreur">{enregistrer.messageErreur}</Bandeau> : null}
            <Bouton variante="principal" chargement={enregistrer.enCours} disabled={Object.values(saisie).filter(Boolean).length === 0} onClick={() => enregistrer.lancer(undefined)}>Enregistrer les présences</Bouton>
          </>
        )}
      </EtatRequete>
    </Panneau>
  );
}

function rapportErreur(erreur: unknown): { lignes: number; erreurs: { ligne: number; code: string }[] } | null {
  if (!(erreur instanceof ErreurApi) || erreur.code !== 'import_presences_invalide') return null;
  return erreur.details as { lignes: number; erreurs: { ligne: number; code: string }[] };
}

function Import() {
  const [fichier, setFichier] = useState<File | null>(null);
  const [rapport, setRapport] = useState<RapportImportPresences | null>(null);
  const action = useAction({
    executer: async () => importerPresences(await (fichier as File).text(), crypto.randomUUID()),
    succes: (r) => (r.deja_importe ? 'Ce fichier a déjà été importé : aucun nouvel effet.' : `${r.acceptees} présence(s) importée(s).`),
    invalider: INVALIDER_PERSONNEL,
    apres: (r) => { setRapport(r); setFichier(null); },
  });
  const refus = rapportErreur(action.erreur);
  return (
    <Panneau titre="Importer des présences (CSV)" niveau={2}>
      <p>Colonnes attendues : <code>matricule</code>, <code>jour</code> (AAAA-MM-JJ) et <code>etat</code> (present, absent, mission ou conge). Le fichier est accepté en entier ou refusé en entier ; une présence déjà saisie avec un autre état ou un doublon du fichier est signalé, jamais remplacé.</p>
      <div className={styles.grilleFormulaire}>
        <FichierChamp libelle="Fichier CSV" multiple={false} accept=".csv,text/csv" tailleMax={2 * 1024 * 1024} fichiers={fichier ? [fichier] : []} onFichiers={(f) => { setFichier(f[0] ?? null); setRapport(null); action.reinitialiser(); }} />
        <Bouton variante="secondaire" chargement={action.enCours} disabled={!fichier} onClick={() => action.lancer(undefined)}>Importer</Bouton>
      </div>
      {refus ? (
        <Bandeau type="erreur">
          Import refusé : {refus.erreurs.length} ligne(s) en erreur sur {refus.lignes}, aucune présence enregistrée.
          <ul>{refus.erreurs.map((e) => <li key={`${e.ligne}-${e.code}`}>Ligne {e.ligne} : {LIBELLES_ERREUR_IMPORT[e.code] ?? e.code}</li>)}</ul>
        </Bandeau>
      ) : action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
      {rapport ? <Bandeau type="succes">{rapport.lignes} ligne(s) lue(s) : {rapport.acceptees} enregistrée(s), {rapport.identiques} déjà identique(s).</Bandeau> : null}
    </Panneau>
  );
}

function Exports() {
  const jour = aujourdhuiIso();
  const [p, setP] = useState({ debut: `${jour.slice(0, 7)}-01`, fin: jour });
  const [erreur, setErreur] = useState<string | null>(null);
  const exporter = (nature: 'presences' | 'absences') => {
    setErreur(null);
    telechargerExport(nature, p.debut, p.fin).catch((e: unknown) => setErreur(e instanceof Error ? e.message : 'Export impossible'));
  };
  return (
    <Panneau titre="Exports pour la paie externe" niveau={2}>
      <p>Présences saisies et absences au titre des congés validés, sans motif ni élément de rémunération. Une même année par export.</p>
      <div className={styles.grilleFormulaire}>
        <Champ libelle="Du" type="date" value={p.debut} onChange={(e) => setP((x) => ({ ...x, debut: e.target.value }))} />
        <Champ libelle="Au" type="date" value={p.fin} onChange={(e) => setP((x) => ({ ...x, fin: e.target.value }))} />
      </div>
      <span className={commun.ligne}>
        <Bouton variante="secondaire" icone={Download} onClick={() => exporter('presences')}>Exporter les présences (CSV)</Bouton>
        <Bouton variante="secondaire" icone={Download} onClick={() => exporter('absences')}>Exporter les absences (CSV)</Bouton>
      </span>
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
    </Panneau>
  );
}

export function PagePresences() {
  const { contexte } = useSession();
  return (
    <>
      <EnTetePage etiquette="Personnel" titre="Présences" description="Une ligne par agent et par jour. Le chef de service saisit pour les agents de ses services ; les ressources humaines habilitées pour tous."
        actions={<LienBouton href="/personnel" icone={ArrowLeft} variante="secondaire">Personnel</LienBouton>} />
      <div className={commun.pile}>
        <Feuille />
        {peut(contexte, 'personnel_sensible', 'modifier') ? <Import /> : null}
        {peut(contexte, 'personnel_sensible', 'exporter') ? <Exports /> : null}
      </div>
    </>
  );
}
